import { VStack, HStack } from '@chakra-ui/react';
import { PortfolioIllustration, Typography } from 'Tokens';
import { CashSovereignsOptions, Portfolio, PortfolioFieldsFragment_ } from 'models';
import { TaxonomyScoreChart } from 'Organisms';
import { Link, useNavigate } from 'react-router-dom';
import { scoreSections } from 'utils/financials';
import { useTranslation } from 'utils/translation';
import { useMemo } from 'react';
import {
  getAggregatePortfolioScores,
  aggregatePortfolioProgress,
  getAggregatePortfolioSubstantialContribution,
} from 'utils/scores/portfolio';
import { Menu } from 'Molecules/Menu';
import { EditIcon } from 'Tokens/Icons/Function';
import { ReportPeriods } from 'Features/PortfolioView';
import { formatDisplayQuarterYear, formatQuarterYear } from 'utils/date';
import { useCashAndSovereigns } from '..';

export const PortfolioNoData = ({
  year,
  portfolioName,
}: {
  year: string;
  portfolioName: string;
}) => {
  const { t } = useTranslation(['common']);
  return (
    <HStack
      justifyContent="center"
      alignItems="start"
      pt="40px"
      spacing="24px"
      width="100%"
      height="178px"
    >
      <VStack>
        <PortfolioIllustration height="72px" width="72px" />
      </VStack>
      <VStack alignItems="start" width="400px">
        <Typography variant="bodyStrong">
          {t('portfolio:noData.title', { year: formatDisplayQuarterYear(year) })}
        </Typography>
        <Typography variant="body">
          {t('portfolio:noData.description', { portfolioName })}
        </Typography>
      </VStack>
    </HStack>
  );
};

export const PortfolioSummary = ({
  portfolioCompanies,
  reportPeriod,
  cashAndSovereigns,
}: {
  portfolioCompanies: Portfolio['portfolioCompanies'];
  reportPeriod: ReportPeriods;
  cashAndSovereigns: CashSovereignsOptions;
}) => {
  const scoresAggregate = useMemo(() => {
    return getAggregatePortfolioScores(portfolioCompanies, reportPeriod, cashAndSovereigns);
  }, [portfolioCompanies, cashAndSovereigns]);

  const totalProgress = useMemo(() => {
    return aggregatePortfolioProgress(portfolioCompanies, reportPeriod, cashAndSovereigns);
  }, [portfolioCompanies, cashAndSovereigns]);

  const scoresSC = useMemo(() => {
    return getAggregatePortfolioSubstantialContribution(
      portfolioCompanies,
      reportPeriod,
      cashAndSovereigns
    );
  }, [portfolioCompanies, cashAndSovereigns]);

  return (
    <HStack justifyContent="space-evenly" spacing="12px" width="100%" height="100%">
      {scoreSections.map((section) => {
        return (
          <TaxonomyScoreChart
            title={section}
            size="lg"
            progress={totalProgress}
            score={scoresAggregate[section]}
            key={section}
            withDetails={true}
            scData={scoresSC}
          />
        );
      })}
    </HStack>
  );
};

const PortfolioItem = ({
  portfolio,
  onEdit,
  reportPeriod,
  year,
  isAuditor = false,
}: {
  portfolio: Portfolio;
  onEdit: (p: Portfolio) => void;
  reportPeriod: ReportPeriods;
  year?: string;
  isAuditor?: boolean;
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const portfolioUrl = `${portfolio.id}/${year}`;
  const latestPortfolioYear = useMemo(() => {
    const allYears = [
      ...new Set(portfolio.portfolioCompanies.map((c) => formatQuarterYear(c.quarter, c.year))),
    ].sort((a, b) => a.localeCompare(b));
    return allYears[allYears.length - 1];
  }, [portfolio, year]);

  const portfolioCompaniesForCurrentYear: PortfolioFieldsFragment_['portfolioCompanies'] =
    useMemo(() => {
      const filterYear = year ?? latestPortfolioYear ?? new Date().getFullYear();
      return portfolio.portfolioCompanies.filter(
        (c) => formatQuarterYear(c.quarter, c.year) === filterYear
      );
    }, [year, latestPortfolioYear, portfolio]);

  const { cash, sovereigns } = useCashAndSovereigns({
    portfolioId: portfolio.id,
    yearAndQuarter: year ?? latestPortfolioYear,
    reportPeriod,
    defaultValue: true,
  });

  return (
    <VStack
      borderRadius="8px"
      _hover={{ bg: 'bg.hover' }}
      _focusWithin={{ bg: 'bg.hover' }}
      borderColor="border.decorative"
      borderWidth="1px"
      borderStyle="solid"
      onClick={() => navigate(portfolioUrl)}
      cursor="pointer"
      alignItems="stretch"
      width="100%"
      p="16px"
      spacing="0px"
    >
      <HStack width="100%" justifyContent="space-between">
        <Link to={portfolioUrl}>
          <Typography variant="h2">{portfolio.name}</Typography>
        </Link>
        <Menu
          sections={[
            {
              actions: [
                {
                  id: 'edit',
                  title: t('common:actions.edit'),
                  onClick: () => onEdit(portfolio),
                  leftElement: <EditIcon color="inherit" />,
                  isDisabled: isAuditor,
                },
              ],
            },
          ]}
        />
      </HStack>
      <HStack>
        <Typography marginBottom="12px" variant="detail" maxW="640px">
          {portfolio.description}
        </Typography>
      </HStack>
      {!!portfolioCompaniesForCurrentYear.length ? (
        <PortfolioSummary
          portfolioCompanies={portfolioCompaniesForCurrentYear}
          reportPeriod={reportPeriod}
          cashAndSovereigns={{ cash, sovereigns }}
        />
      ) : (
        <PortfolioNoData
          year={year ?? latestPortfolioYear ?? `Q1-${new Date().getFullYear().toString()}`}
          portfolioName={portfolio.name}
        />
      )}
    </VStack>
  );
};

export const PortfoliosList = ({
  portfolios,
  onEdit,
  year,
  isAuditor,
}: {
  portfolios: PortfolioFieldsFragment_[];
  onEdit: (p: Portfolio) => void;
  year: string;
  isAuditor?: boolean;
}) => {
  return (
    <VStack paddingY="16px" spacing="16px" alignItems="stretch" width="100%">
      {portfolios.map((p) => (
        <PortfolioItem
          portfolio={p}
          key={p.id}
          onEdit={onEdit}
          reportPeriod={ReportPeriods.year}
          year={year}
          isAuditor={isAuditor}
        />
      ))}
    </VStack>
  );
};
